'use client';

import type { AdvisorStagedClient } from 'src/features/advisor-book-onboarding/utils/staged-book';
import { Label } from '@savvy-kit/components/label';
import { EditableContactCell } from 'src/features/advisor-book-onboarding/components/upload/editable-contact-cell';
import {
  getClientFullName,
  getContactFieldState,
  getImportErrorMessage,
  getSheetEditableNameErrorMessage,
} from 'src/features/advisor-book-onboarding/utils/staged-book';
import { formatPhoneNumber } from 'src/utils/formatting';

type UpdateStagedClientFieldParams = {
  readonly clientId: string;
  readonly field: 'email' | 'phone';
  readonly value: string;
};

type UpdateStagedClientNameParams = {
  readonly clientId: string;
  readonly fullName: string;
  readonly originalFirstName?: string | null;
  readonly originalLastName?: string | null;
};

export type StagedClientContactFieldsProps = {
  readonly client: AdvisorStagedClient;
  readonly savingClientId: string | undefined;
  readonly onUpdateClientField: (params: UpdateStagedClientFieldParams) => Promise<boolean>;
  readonly onUpdateClientName: (params: UpdateStagedClientNameParams) => Promise<boolean>;
};

export function StagedClientContactFields({
  client,
  savingClientId,
  onUpdateClientField,
  onUpdateClientName,
}: StagedClientContactFieldsProps) {
  const clientName = getClientFullName(client);
  const isSaving = savingClientId === client.id;
  const emailState = getContactFieldState(client, 'email');
  const phoneState = getContactFieldState(client, 'phone');
  const roleLabel = client.role === 'co' ? 'Co-client' : 'Primary client';
  const accessibleName = clientName.trim() !== '' ? clientName : `client ${client.id}`;
  const nameId = `staged-client-name-${client.id}`;
  const emailId = `staged-client-email-${client.id}`;
  const phoneId = `staged-client-phone-${client.id}`;
  const emailError = getImportErrorMessage(client.importErrors, 'email');
  const phoneError = getImportErrorMessage(client.importErrors, 'phone');
  const nameError = getSheetEditableNameErrorMessage(client);

  return (
    <section className="flex flex-col gap-4" aria-labelledby={`staged-client-heading-${client.id}`}>
      <h2 id={`staged-client-heading-${client.id}`} className="text-sm font-semibold text-dark">
        {roleLabel}
      </h2>

      <div className="flex flex-col gap-0.5">
        <Label htmlFor={nameId}>Name</Label>
        <EditableContactCell
          id={nameId}
          value={clientName}
          field="text"
          ariaLabel={`Edit name for ${accessibleName}`}
          saving={isSaving}
          errorMessage={nameError}
          onCommit={(nextValue) => {
            void onUpdateClientName({
              clientId: client.id,
              fullName: nextValue,
              originalFirstName: client.firstName,
              originalLastName: client.lastName,
            });
          }}
        />
      </div>

      <div className="flex flex-col gap-0.5">
        <Label htmlFor={emailId}>Email</Label>
        <EditableContactCell
          id={emailId}
          value={emailState.value}
          field="email"
          ariaLabel={`Edit email for ${accessibleName}`}
          saving={isSaving}
          errorMessage={emailError}
          onCommit={(nextValue) => {
            void onUpdateClientField({ clientId: client.id, field: 'email', value: nextValue });
          }}
        />
      </div>

      <div className="flex flex-col gap-0.5">
        <Label htmlFor={phoneId}>Phone</Label>
        <EditableContactCell
          id={phoneId}
          value={phoneState.value}
          field="phone"
          ariaLabel={`Edit phone for ${accessibleName}`}
          saving={isSaving}
          displayValue={phoneState.value.trim() === '' ? undefined : formatPhoneNumber(phoneState.value)}
          errorMessage={phoneError}
          onCommit={(nextValue) => {
            void onUpdateClientField({ clientId: client.id, field: 'phone', value: nextValue });
          }}
        />
      </div>
    </section>
  );
}
